import React, {PropsWithChildren} from 'react';
import {DataGridProProps, GridColDef, GridRowsProp} from '@mui/x-data-grid-pro';
import {Box} from '@mui/material';
import * as Sentry from '@sentry/react';

import EditorToolBar from './EditorToolbar';
import {useDebugContext} from '../../contexts/DebugProvider';
import LoadingOverlay from './LoadingOverlay';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import {StickyDataGrid} from '../Common/StickyDataGrid';
import {SaltusAny} from '../../utils/SaltusAny';
import {CustomColumnMenuComponent, useDataGridState} from './useDataGridState';
import Loading from '../Loading/Loading';

export const EditorGrid: React.FC<
  PropsWithChildren<EditorGridProps & {dataGridId: string}>
> = ({
  dataGridId,
  columns,
  rows,
  loading,
  error,
  createFunc,
  createFuncLoading,
  updateFunc,
  deleteFunc,
  submitFunc,
  syncFunc,
  onRowDoubleClick,
  readOnly = false,
  onSelected,
  children,
  rowsFiltered,
  filterRowsFunc,
  rowsFilteredLabel,
  rowsFilteredTooltip,
  minHeight = '300px',
  initialState: defaultInitialState,
  ...dataGridProps
}) => {
  const {apiRef, initialState, key, restoreState} = useDataGridState(dataGridId);
  const [selectedId, setSelectedId] = React.useState<string | null | number>(null);
  const {debug} = useDebugContext();
  const showErrorState = error || debug?.editorGridFailure;
  const showLoadingState = loading || debug?.editorGridLoading;
  const shownRows = debug?.editorGridEmpty ? [] : rows;

  if (initialState === undefined) {
    return <Loading />;
  }

  if (showErrorState) {
    return <ErrorMessage error={error} />;
  }

  return (
    <Box minHeight={minHeight} display="flex" flexDirection="column">
      <StickyDataGrid
        {...(dataGridProps as DataGridProProps)}
        sx={{
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
        data-test="Data grid"
        // error={showErrorState ? true : undefined}
        loading={showLoadingState}
        density="standard"
        apiRef={apiRef}
        key={key}
        initialState={initialState || defaultInitialState}
        rows={shownRows}
        columns={columns}
        onRowDoubleClick={(params, event, details) => {
          const name = params.row?.name || params.row?.id;
          if (name) {
            Sentry.addBreadcrumb({
              category: 'UI Click',
              message: `Row double clicked: ${name}`,
              level: 'info',
            });
          }
          onRowDoubleClick?.(params, event, details);
        }}
        onRowClick={
          readOnly
            ? undefined
            : (e) => {
                if (onSelected) onSelected(e.row);
                setSelectedId(e.id);
              }
        }
        slots={{
          columnMenu: (props) => (
            <CustomColumnMenuComponent restoreState={restoreState} {...props} />
          ),
          toolbar: (toolbarProps) =>
            readOnly
              ? null // dont show toolbar if readonly
              : EditorToolBar({
                  ...toolbarProps,
                  rowsFiltered,
                  children,
                  filterRowsFunc,
                  rowsFilteredLabel,
                  rowsFilteredTooltip,
                  syncFunc,
                  createFunc,
                  createFuncLoading,
                  updateFunc,
                  deleteFunc,
                  setSelectedId,
                }),
          loadingOverlay: LoadingOverlay,
        }}
        // note, this is correct but material ui's types are wrong
        slotProps={{
          toolbar: {
            // @ts-ignore
            createFunc,
            updateFunc,
            deleteFunc,
            submitFunc,
            selectedId,
            setSelectedId,
            syncFunc,
          },
        }}
      />
    </Box>
  );
};

type EditorGridProps = Omit<DataGridProProps, 'rows' | 'autoHeight'> & {
  columns: GridColDef[];
  rows: GridRowsProp;
  error?: SaltusAny;
  loading?: boolean;
  readOnly?: boolean;
  createFunc: boolean | (() => void);
  createFuncLoading: boolean;
  updateFunc: boolean | ((selectedId: string | null) => void);
  deleteFunc?: (args: SaltusAny) => SaltusAny;
  submitFunc?: (args: SaltusAny) => SaltusAny;
  syncFunc: boolean | ((reviewID: string | null) => void);
  onSelected?: (args: SaltusAny) => SaltusAny;
  rowsFiltered?: boolean;
  deleteFuncLoading?: boolean;
  filterRowsFunc?: (args: SaltusAny) => SaltusAny;
  rowsFilteredLabel?: string;
  rowsFilteredTooltip?: string;
  minHeight: string | number;
};

export default EditorGrid;
